/**
 * Normalization of a backend error body into the fields `ApiError` carries.
 *
 * Two envelope shapes are in the wild across Noor's backends:
 *
 *   flat    { message, code, details }
 *   nested  { error: { message, code, details } }   ← the Eats merchant gateway
 *
 * The nested form is what `openapi.json`'s `ErrorResponse` documents and what
 * `test.api.noor.uz` actually returns, e.g.
 *
 *   { "error": { "code": 1006, "message": "invalid input",
 *                "details": [{ "field": "id", "issue": "must be a valid UUID" }] } }
 *
 * `code` is a number there (an app-specific error catalog) while the flat form
 * uses a string, so it is stringified into `ApiError.code`.
 */

/** The subset of `ApiError` fields recoverable from a response body. */
export interface NormalizedErrorBody {
  message?: string;
  code?: string;
  details?: unknown;
}

/**
 * Pull `{ message, code, details }` out of an error body of either envelope
 * shape. Unknown/absent keys are simply omitted, so the caller keeps control of
 * its own fallbacks. Never throws — a malformed body yields `{}`.
 */
export function normalizeErrorBody(body: unknown): NormalizedErrorBody {
  const source = unwrapErrorEnvelope(body);
  if (source === undefined) return {};

  const normalized: NormalizedErrorBody = {};

  const message = source.message;
  if (typeof message === 'string' && message.trim() !== '') {
    normalized.message = message;
  }

  // Numeric on the nested envelope, string on the flat one — both become the
  // string `ApiError.code`. `0` is a legitimate code, so no truthiness test.
  const code = source.code;
  if (typeof code === 'string' && code.trim() !== '') normalized.code = code;
  else if (typeof code === 'number' && Number.isFinite(code)) {
    normalized.code = String(code);
  }

  if (source.details !== undefined) normalized.details = source.details;

  return normalized;
}

/** The error-bearing object: `body.error` when nested, else `body` itself. */
function unwrapErrorEnvelope(body: unknown): Record<string, unknown> | undefined {
  if (body === null || typeof body !== 'object') return undefined;
  const record = body as Record<string, unknown>;
  const nested: unknown = record.error;
  if (nested !== null && typeof nested === 'object') {
    return nested as Record<string, unknown>;
  }
  return record;
}
